import { Tooltip } from "@mui/material";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import ProjectApi from "../../../../../../api/projectApi";
import CircleAvatar from "../../../../../../components/CircleAvatar/CircleAvatar";
import ConfirmationModal from "../../../../../../components/ConfirmationModal/ConfirmationModal";
import { ISO639Lang } from "../../../../../../constans/languagesList";
import {
  Project,
  TypeProjectParticipant,
} from "../../../../../../types/Company";
import { getTranslation } from "../../../../../../utils/getTranslation";
import { SuccesNotify } from "../../../../../../utils/toaster";
import s from "./SpecialistCardBlock.module.css";

type UsersToInvite = {
  specialization: string[];
  email: string;
};

type SpecialistCardBlockProps = {
  user: TypeProjectParticipant;
  bgColor: string;
  discount: number;
  projectId: string;
  setProjectSpecialists: Dispatch<SetStateAction<TypeProjectParticipant[]>>;
  isInviteCard?: boolean;
  specialistsToInvite: UsersToInvite[];
  setSpecialistsToInvite: Dispatch<SetStateAction<UsersToInvite[]>>;
  specializationsData?: {
    specialistId: string;
    specialization: string;
    specializationId: string;
    disabled?: boolean;
  }[];
  projectData: Project;
  availableSpecializations?: string[];
};

type ProgramUserData = {
  name: string;
  surname: string;
  price: { amount: number; language: string }[];
  specializationsData: {
    specialistId: string;
    specialization: string;
    specializationId: string;
    disabled?: boolean;
  }[];
  email: string;
};

const SpecialistCardBlock = ({
  user,
  bgColor,
  discount,
  projectId,
  setProjectSpecialists,
  isInviteCard,
  specialistsToInvite,
  setSpecialistsToInvite,
  specializationsData,
  projectData,
  availableSpecializations,
}: SpecialistCardBlockProps) => {
  const token = localStorage.getItem("token");
  const [specialistData, setSpecialistData] = useState<ProgramUserData>();
  const [deleteSpecModal, setDeleteSpecModal] = useState(false);
  const [sessionPrice, setSessionPrice] = useState<
    {
      priceWithDiscount: string;
      generalPrice: string;
      language: string;
    }[]
  >([]);

  useEffect(() => {
    const userData = {
      ...user,
      specialistIds: availableSpecializations
        ? user.specialistIds?.filter((item) =>
            availableSpecializations?.includes(item._id),
          )
        : user.specialistIds,
    };

    const formattedData: ProgramUserData = {
      name: getTranslation(userData.name),
      surname: getTranslation(userData.surname),
      email: userData.email!,
      price: userData.specialistIds![0].price || [],
      specializationsData:
        specializationsData ||
        userData.specialistIds!.map((item) => ({
          specialization: item.mainSpecializations[0].specialization,
          specialistId: item._id,
          specializationId: item.mainSpecializations[0]._id,
          disabled: !item.servicesConfig.showInProjects.switch,
        })),
    };
    setSpecialistData(formattedData);
  }, [token, user, specializationsData]);

  useEffect(() => {
    if (!specialistData || !specialistData.price) return;

    const prices = specialistData.price.map((item) => ({
      priceWithDiscount:
        discount === 100
          ? "0"
          : (item.amount - item.amount * (discount / 100)).toFixed(0),
      generalPrice: item.amount.toString(),
      language: item.language,
    }));

    setSessionPrice(prices);
  }, [discount, specialistData]);

  const removeSpecialist = async () => {
    if (!token) return;

    const response = await ProjectApi.removeUserFromProject(token, {
      userIdToRemove: user._id,
      projectId,
    });

    if (response.status) {
      SuccesNotify("Specialist has been removed from the project");
      setProjectSpecialists((prev) =>
        prev.filter((spec) => spec._id !== user._id),
      );
      setDeleteSpecModal(false);
    }
  };

  const toggleSpecialistSelection = (specializationId: string) => {
    if (!specialistData) return;

    const isSelected = specialistsToInvite.some(
      (item) =>
        item.email === specialistData.email &&
        item.specialization.includes(specializationId),
    );

    if (isSelected) {
      setSpecialistsToInvite((prev) =>
        prev.filter(
          (item) =>
            item.email !== specialistData.email ||
            !item.specialization.includes(specializationId),
        ),
      );
    } else {
      setSpecialistsToInvite((prev) => [
        ...prev,
        {
          email: specialistData.email,
          specialization: [specializationId],
        },
      ]);
    }
  };

  return (
    <div className={s.container} style={{ backgroundColor: bgColor }}>
      <div className={s.mainSpecInfoBlock}>
        <CircleAvatar userId={user._id} width="50px" height="50px" />
        <div className={s.mainSpecTitleInfo}>
          <span>
            {specialistData?.name} {specialistData?.surname}
          </span>
        </div>
      </div>
      <div className={s.mainSpecInfoBlock}>{specialistData?.email}</div>
      <div className={s.specalizationTitleBlock}>
        {specialistData?.specializationsData.map((item) => (
          <Tooltip
            key={item.specializationId}
            arrow
            title={
              item.disabled
                ? "This specialist did not enable the 'Participation in projects' service"
                : projectData.pausedSpecialists?.includes(item.specialistId)
                  ? "This specialist has suspended participation in the project in this specialty."
                  : ""
            }
            disableHoverListener={
              !item.disabled &&
              !projectData.pausedSpecialists?.includes(item.specialistId)
            }
          >
            <div
              className={`${
                specialistsToInvite.some(
                  (spec) =>
                    spec.email === specialistData.email &&
                    spec.specialization.includes(item.specializationId),
                )
                  ? s.activeSpecialization
                  : s.notActiveSpecialiation
              } ${
                projectData.pausedSpecialists?.includes(item.specialistId)
                  ? s.pausedSpec
                  : ""
              }`}
              style={{ opacity: item.disabled ? 0.5 : 1 }}
              onClick={() =>
                !item.disabled && isInviteCard
                  ? toggleSpecialistSelection(item.specializationId)
                  : undefined
              }
            >
              {item.specialization}
            </div>
          </Tooltip>
        ))}
      </div>
      {specialistData && (
        <div className={s.priceActionBlock}>
          <div className={s.prcieBlock}>
            {sessionPrice.map((item, index) => (
              <div className={s.priceLement} key={index}>
                <span>{ISO639Lang[item.language]}</span>
                <span>-</span>
                <div className={s.priceValue}>
                  {discount > 0 ? (
                    <span>{item.generalPrice}$</span>
                  ) : (
                    <span></span>
                  )}
                  <span>{item.priceWithDiscount}$</span>
                </div>
              </div>
            ))}
          </div>
          {!isInviteCard && (
            <div className={s.actionBlock}>
              <div
                className={s.deleteBtn}
                role="button"
                onClick={() => setDeleteSpecModal(true)}
              >
                Remove
              </div>
            </div>
          )}
        </div>
      )}
      <ConfirmationModal
        confirmFunc={removeSpecialist}
        confirmTitle={`Are you sure you want to remove ${specialistData?.name} ${specialistData?.surname} from this project?`}
        modalVisible={deleteSpecModal}
        setModalVisible={setDeleteSpecModal}
      />
    </div>
  );
};

export default SpecialistCardBlock;
